import React, { useState } from 'react';
import { FileNode, OpenFile } from '../../types/types';
import { FileTextOutlined, FolderOpenOutlined, FolderOutlined } from '@ant-design/icons';

interface FileTreeProps {
  fileSystem: Map<string, FileNode>;
  onFileClick: (id: string) => void;
  onFolderToggle: (id: string) => void;
  onContextMenu: (e: React.MouseEvent, targetId: string) => void;
  activeFileId: string | null;
  openFiles: Map<string, OpenFile>;
}

const FileTree: React.FC<FileTreeProps> = ({
  fileSystem,
  onFileClick,
  onFolderToggle,
  onContextMenu,
  activeFileId,
  openFiles,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const renderNode = (node: FileNode, depth = 0): React.ReactNode => {
    if (node.id === 'root') {
      return (
        <div key={node.id}>{Array.from(node.children?.values() || []).map(child => renderNode(child, depth))}</div>
      );
    }

    const isSelected = selectedId === node.id;
    const isActive = activeFileId === node.id;
    const isModified = node.type === 'file' && openFiles.get(node.id)?.modified;

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedId(node.id);

      if (node.type === 'file') {
        onFileClick(node.id);
      } else {
        onFolderToggle(node.id);
      }
    };

    const handleContextMenu = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedId(node.id);
      onContextMenu(e, node.id);
    };

    return (
      <div key={node.id}>
        <div
          className={`file-tree-item ${node.type} ${isSelected ? 'selected' : ''} ${isActive ? 'active' : ''} ${
            isModified ? 'modified' : ''
          }`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
        >
          <span className="icon">
            {node.type === 'folder' ? (
              node.expanded ? (
                <FolderOpenOutlined />
              ) : (
                <FolderOutlined />
              )
            ) : (
              <FileTextOutlined />
            )}
          </span>
          <span className="label">{node.name}</span>
          {isModified && <span className="modified-indicator">●</span>}
        </div>

        {node.type === 'folder' && node.expanded && node.children && (
          <div>{Array.from(node.children.values()).map(child => renderNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  const rootNode = fileSystem.get('root');
  if (!rootNode) return null;

  return <div className="file-tree">{renderNode(rootNode)}</div>;
};

export default FileTree;
